import React, { useMemo, useState, useEffect } from 'react';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Component for commit comparison
const CommitComparisonSummary = ({ projectId, relevantMetrics = [] }) => {
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (projectId) {
      fetchComparisonData();
    }
  }, [projectId]);


  const fetchComparisonData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/projects/${projectId}/commits/compare/`);
      if (response.ok) {
        const data = await response.json();
        setComparisonData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch comparison data');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatPercentageChange = (change) => {
    const absChange = Math.abs(change);
    const sign = change > 0 ? '+' : change < 0 ? '-' : '';
    return `${sign}${absChange.toFixed(1)}%`;
  };

  const getChangeColor = (change, isSmellMetric = false, isFitnessMetric = false) => {
    if (change === 0) return '#6c757d';

    // Special handling for fitness - higher is better
    if (isFitnessMetric) {
      return change > 0 ? '#198754' : '#dc3545';
    }

    // For smell metrics, lower is better
    if (isSmellMetric) {
      return change < 0 ? '#198754' : '#dc3545';
    } else {
      // For other metrics (LOC, classes, etc.), higher is generally better
      return change > 0 ? '#198754' : '#dc3545';
    }
  };

  const getChangeIcon = (change, isSmellMetric = false, isFitnessMetric = false) => {
    if (change === 0) return '=';

    // Special handling for fitness - higher is better
    if (isFitnessMetric) {
      return change > 0 ? '↑' : '↓';
    }

    // For smell metrics, lower is better
    if (isSmellMetric) {
      return change < 0 ? '↓' : '↑';
    } else {
      // For other metrics, show direction as-is
      return change > 0 ? '↑' : '↓';
    }
  };

  const formatMetricValue = (key, value) => {
    if (key === 'fitness') {
      return value.toFixed(2); // Show fitness with 2 decimal places
    }
    return value.toLocaleString();
  };

  const formatMetricName = (key) => {
    const nameMap = {
      'fitness': 'Code Fitness',
      'total_loc': 'Lines of Code',
      'total_packages': 'Packages',
      'total_classes': 'Classes',
      'total_modules': 'Modules',
      'num_implementation_smells': 'Implementation Smells',
      'num_design_smells': 'Design Smells',
      'num_architecture_smells': 'Architecture Smells',
      'num_testability_smells': 'Testability Smells',
      'num_test_smells': 'Test Smells',
      'total_smells': 'Total Smells'
    };
    return nameMap[key] || key.replace(/^num_/, '').replace(/_/g, ' ').replace(/^\w/, l => l.toUpperCase());
  };

  const getFilteredMetrics = () => {
    if (!comparisonData || !relevantMetrics.length) return {};

    const { comparison } = comparisonData;
    const filteredMetrics = {};

    relevantMetrics.forEach(metric => {
      if (comparison.metrics[metric]) {
        filteredMetrics[metric] = comparison.metrics[metric];
      }
    });

    return filteredMetrics;
  };

  if (!projectId || !relevantMetrics.length) return null;

  if (loading) {
    return (
      <div className="mt-3 p-3" style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <div className="text-center">
          <small className="text-muted">Loading comparison data...</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 p-3" style={{
        backgroundColor: '#fff3cd',
        borderRadius: '8px',
        border: '1px solid #ffeaa7',
        color: '#856404'
      }}>
        <div className="text-center">
          <small>{error}</small>
        </div>
      </div>
    );
  }

  if (!comparisonData) return null;

  const { comparison } = comparisonData;
  const filteredMetrics = getFilteredMetrics();
  const smellMetrics = ['num_implementation_smells', 'num_design_smells', 'num_architecture_smells',
    'num_testability_smells', 'num_test_smells', 'total_smells'];

  if (Object.keys(filteredMetrics).length === 0) return null;

  return (
    <div className="mt-3">
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e0e6ed',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        overflow: 'hidden'
      }}>
        <div style={{
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e0e6ed',
          padding: '16px 20px 12px',
        }}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                marginRight: '8px'
              }}></div>
              <h6 style={{
                margin: 0,
                fontWeight: '600',
                color: '#374151',
                fontSize: '14px'
              }}>
                Comparison of last 2 commits
              </h6>
            </div>
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              backgroundColor: '#f3f4f6',
              padding: '4px 8px',
              borderRadius: '6px',
              fontFamily: 'monospace'
            }}>
              {comparison.latest_commit.short_id} ← {comparison.previous_commit.short_id}
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="d-flex flex-wrap justify-content-center gap-3">
            {Object.entries(filteredMetrics).map(([key, data]) => {
              const isSmellMetric = smellMetrics.includes(key);
              const isFitnessMetric = key === 'fitness';
              const changeColor = getChangeColor(data.change, isSmellMetric, isFitnessMetric);
              const changeIcon = getChangeIcon(data.change, isSmellMetric, isFitnessMetric);

              return (
                <div key={key} style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #f3f4f6',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  textAlign: 'center',
                  minWidth: '120px',
                  flex: '1 1 auto',
                  maxWidth: '200px'
                }}>
                  <div style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    {formatMetricName(key)}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    {formatMetricValue(key, data.current)}
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: changeColor === '#198754' ? '#dcfce7' : changeColor === '#dc3545' ? '#fef2f2' : '#f3f4f6',
                    color: changeColor,
                    padding: '1px 6px',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    <span style={{ marginRight: '1px' }}>{changeIcon}</span>
                    {formatPercentageChange(data.change)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const TrendChart = ({
  data,
  dataKeys,
  title,
  colors = [],
  projectId = null,
  onDateClick = null,
  // NEW PROPS - passed from parent
  timeRange = 'all',
  maxDataPoints = 100,
  useLocalTime = true
}) => {
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  const defaultColors = [
    "#06b6d4", "#f59e0b", "#6366f1", "#ef4444",
    "#8b5cf6"
  ];

  // Data processing and filtering (removed internal state)
  const { processedData, filteredCount } = useMemo(() => {
    if (!data || data.length === 0) return { processedData: [], filteredCount: 0 };

    let filteredData = [...data];

    // Sort by date first
    filteredData.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // Apply time range filter
    if (timeRange !== 'all') {
      const now = new Date();
      let startDate, endDate;

      if (useLocalTime) {
        // LOCAL TIMEZONE FILTERING
        switch (timeRange) {
          case 'today':
            startDate = new Date();
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            break;

          case 'yesterday':
            startDate = new Date();
            startDate.setDate(now.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setDate(now.getDate() - 1);
            endDate.setHours(23, 59, 59, 999);
            break;

          case '2d':
            startDate = new Date();
            startDate.setDate(now.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            break;

          case '7d':
            startDate = new Date();
            startDate.setDate(now.getDate() - 7);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            break;

          case '30d':
            startDate = new Date();
            startDate.setDate(now.getDate() - 30);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            break;

          case '90d':
            startDate = new Date();
            startDate.setDate(now.getDate() - 90);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            break;

          case '1y':
            startDate = new Date();
            startDate.setFullYear(now.getFullYear() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            break;
        }
      } else {
        // UTC FILTERING
        switch (timeRange) {
          case 'today':
            startDate = new Date();
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCHours(23, 59, 59, 999);
            break;

          case 'yesterday':
            startDate = new Date();
            startDate.setUTCDate(now.getUTCDate() - 1);
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCDate(now.getUTCDate() - 1);
            endDate.setUTCHours(23, 59, 59, 999);
            break;

          case '2d':
            startDate = new Date();
            startDate.setUTCDate(now.getUTCDate() - 1);
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCHours(23, 59, 59, 999);
            break;

          case '7d':
            startDate = new Date();
            startDate.setUTCDate(now.getUTCDate() - 7);
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCHours(23, 59, 59, 999);
            break;

          case '30d':
            startDate = new Date();
            startDate.setUTCDate(now.getUTCDate() - 30);
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCHours(23, 59, 59, 999);
            break;

          case '90d':
            startDate = new Date();
            startDate.setUTCDate(now.getUTCDate() - 90);
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCHours(23, 59, 59, 999);
            break;

          case '1y':
            startDate = new Date();
            startDate.setUTCFullYear(now.getUTCFullYear() - 1);
            startDate.setUTCHours(0, 0, 0, 0);
            endDate = new Date();
            endDate.setUTCHours(23, 59, 59, 999);
            break;
        }
      }

      filteredData = filteredData.filter(item => {
        const itemDate = new Date(item.created_at);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    const filteredCount = filteredData.length;

    if (filteredData.length > maxDataPoints) {
      return {
        processedData: intelligentSampling(filteredData, maxDataPoints),
        filteredCount
      };
    }

    return { processedData: filteredData, filteredCount };
  }, [data, timeRange, maxDataPoints, useLocalTime]);

  // Intelligent sampling algorithm
  const intelligentSampling = (data, targetPoints) => {
    if (data.length <= targetPoints) return data;

    const sampled = [];
    const step = data.length / targetPoints;

    // Always include first and last points
    sampled.push(data[0]);

    // Sample points using Douglas-Peucker-inspired approach
    for (let i = 1; i < targetPoints - 1; i++) {
      const index = Math.floor(i * step);
      sampled.push(data[index]);
    }

    // Always include the last point
    if (data.length > 1) {
      sampled.push(data[data.length - 1]);
    }

    return sampled;
  };

  // Format functions
  const formatDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") {
      return "Invalid Date";
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }

    // Adjust format based on data density
    const dataCount = processedData.length;
    if (dataCount > 50) {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      });
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatNumber = (value) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    return value.toString();
  };

  // Event handlers
  const handleDateClick = (dateStr) => {
    if (onDateClick && projectId && dateStr) {

      setSelectedDataPoint(dateStr);
      onDateClick(dateStr, projectId);
    }
  };

  const handleChartClick = (data) => {
    if (data && data.activeLabel) {
      handleDateClick(data.activeLabel);
    }
  };

  // Enhanced X-Axis tick click handler
  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;
    const isClickable = onDateClick && projectId;
    const isSelected = selectedDataPoint === payload.value;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill={isSelected ? "#2563eb" : "#666"}
          fontSize={11}
          style={{
            cursor: isClickable ? 'pointer' : 'default',
            fontWeight: isSelected ? 'bold' : 'normal',
            textDecoration: isClickable ? 'underline' : 'none'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isClickable) {
              handleDateClick(payload.value);
            }
          }}
          onMouseEnter={(e) => {
            if (isClickable) {
              e.target.style.fill = '#2563eb';
            }
          }}
          onMouseLeave={(e) => {
            if (isClickable && !isSelected) {
              e.target.style.fill = '#666';
            }
          }}
        >
          {formatDate(payload.value)}
        </text>
        {isClickable && (
          <text
            x={0}
            y={30}
            textAnchor="middle"
            fill="#94a3b8"
            fontSize={9}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleDateClick(payload.value);
            }}
          >

          </text>
        )}
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isClickable = onDateClick && projectId;
      const isSelected = selectedDataPoint === label;

      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: 'white',
            padding: '15px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '200px'
          }}
        >
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              fontSize: '14px',
              color: isClickable ? '#2563eb' : '#333',
              borderBottom: '1px solid #eee',
              paddingBottom: '8px',
              cursor: isClickable ? 'pointer' : 'default',
              textDecoration: isClickable ? 'underline' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => isClickable && handleDateClick(label)}
          >
            <span>{formatDate(label)}</span>
            {isClickable && (
              <span style={{
                fontSize: '12px',
                opacity: 0.8,
                backgroundColor: '#f1f5f9',
                padding: '2px 6px',
                borderRadius: '4px'
              }}>
                {isSelected ? '✓ Selected' : 'Click to view'}
              </span>
            )}
          </div>
          {payload
            .sort((a, b) => b.value - a.value)
            .map((entry, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '4px 0',
                fontSize: '13px'
              }}>
                <span style={{
                  color: entry.color,
                  fontWeight: '500',
                  marginRight: '10px'
                }}>
                  {entry.name}:
                </span>
                <span style={{
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  {entry.dataKey === 'fitness'
                    ? entry.value.toFixed(2)
                    : entry.value.toLocaleString()
                  }
                </span>
              </div>
            ))}
        </div>
      );
    }
    return null;
  };

  // Enhanced dot component with better visual feedback
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;

    if (!payload || !payload.created_at) return null;

    const isClickable = onDateClick && projectId;
    const isSelected = selectedDataPoint === payload.created_at;

    // Show dots if we have fewer points OR if this point is selected
    const shouldShowDot = processedData.length <= 50 || isSelected;

    if (!shouldShowDot && !isSelected) return null;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={isSelected ? 6 : 4}
        fill={isSelected ? "#2563eb" : "white"}
        stroke={isSelected ? "#2563eb" : props.stroke}
        strokeWidth={isSelected ? 3 : 2}
        style={{
          cursor: isClickable ? 'pointer' : 'default',
          filter: isSelected ? 'drop-shadow(0 0 6px rgba(37, 99, 235, 0.4))' : 'none'
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (isClickable) handleDateClick(payload.created_at);
        }}
      />
    );
  };

  // Format field names
  const formatFieldName = (key) => {
    const fieldNames = {
      total_modules: 'Modules',
      total_classes: 'Classes',
      total_packages: 'Packages',
      total_loc: 'Lines of Code',
      num_implementation_smells: 'Implementation Smells',
      num_design_smells: 'Design Smells',
      num_architecture_smells: 'Architecture Smells',
      num_testability_smells: 'Testability Smells',
      num_test_smells: 'Test Smells',
      total_smells: 'Total Smells',
      fitness: 'Code Fitness'
    };
    return fieldNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center p-5">
        <div style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.3 }}></div>
        <h6 className="text-muted mb-2">No Trend Data Available</h6>
        <p className="text-muted small">Analyze your project multiple times to see trends over time</p>
      </div>
    );
  }

  if (data.length === 1) {
    return (
      <div className="text-center p-5">
        <div style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.5 }}></div>
        <h6 className="text-muted mb-2">Insufficient Data for Trends</h6>
        <p className="text-muted small">Need at least 2 analysis points to show trends. Current data points: {data.length}</p>
      </div>
    );
  }

  const isClickable = onDateClick && projectId;

  return (
    <div style={{ width: '100%', minHeight: '450px' }}>
      {title && (
        <div className="mb-4 text-center">
          <h6 style={{ fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
            {title}
          </h6>
          <div className="d-flex justify-content-center align-items-center gap-3">
            <small className="text-muted">
              Showing {processedData.length} of {data.length} data points
              {data.length !== processedData.length && ` (${data.length - processedData.length} points filtered)`}
            </small>
            {isClickable && (
              <small style={{
                color: '#2563eb',
                backgroundColor: '#eff6ff',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: '500'
              }}>
                Click dates to view detailed analysis
              </small>
            )}
          </div>
          {selectedDataPoint && (
            <div style={{
              marginTop: '8px',
              padding: '4px 12px',
              backgroundColor: '#dcfce7',
              color: '#166534',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '500',
              display: 'inline-block'
            }}>
              ✓ Selected: {formatDate(selectedDataPoint)}
            </div>
          )}
        </div>
      )}

      <ResponsiveContainer width="100%" height={350}>
        <AreaChart
          data={processedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          onClick={handleChartClick}
        >
          <defs>
            {dataKeys.map((key, index) => {
              const color = colors[index] || defaultColors[index % defaultColors.length];
              return (
                <linearGradient key={`gradient-${key}`} id={`gradient-${key}`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                  <stop offset="50%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                </linearGradient>
              );
            })}
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" strokeOpacity={0.6} />

          <XAxis
            dataKey="created_at"
            tick={<CustomXAxisTick />}
            interval="preserveStartEnd"
            axisLine={{ stroke: '#d0d0d0' }}
            tickLine={{ stroke: '#d0d0d0' }}
            tickCount={processedData.length > 50 ? 6 : 10}
            height={60}
          />

          <YAxis
            tick={{ fontSize: 11, fill: '#666' }}
            tickFormatter={formatNumber}
            width={70}
            axisLine={{ stroke: '#d0d0d0' }}
            tickLine={{ stroke: '#d0d0d0' }}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }} iconType="rect" />

          {dataKeys.map((key, index) => {
            const color = colors[index] || defaultColors[index % defaultColors.length];
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                strokeWidth={processedData.length > 100 ? 1.5 : 2.5}
                fill={`url(#gradient-${key})`}
                fillOpacity={0.6}
                dot={<CustomDot />}
                activeDot={{ r: 8, stroke: color, strokeWidth: 3, fill: 'white' }}
                name={formatFieldName(key)}
                connectNulls={false}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>

      <CommitComparisonSummary projectId={projectId} relevantMetrics={dataKeys} />
    </div>
  );
};

export default TrendChart;